import React, { useState } from 'react';
import { Calculator, Plus, Equal, Save, History } from 'lucide-react';
import { UnitCategory } from '../types/units';
import { convertValue } from '../utils/conversions';
import { unitsByCategory } from '../utils/units';

const descriptions = {
  title: "Multi-Step Calculator",
  description: "Chain multiple conversions and calculations together. Perfect for complex unit transformations and multi-step calculations.",
  features: [
    "Combine multiple conversions in sequence",
    "Perform mathematical operations between steps",
    "Save and load calculation chains",
    "View step-by-step results"
  ]
};

interface Step {
  value: string;
  fromUnit: string;
  category: UnitCategory;
  operation: '+' | '-' | '*' | '/' | '=' | 'convert';
}

interface ToolsPanelProps {
  category: UnitCategory;
}

export const ToolsPanel: React.FC<ToolsPanelProps> = ({ category }) => {
  const [steps, setSteps] = useState<Step[]>([]);
  const [currentValue, setCurrentValue] = useState<string>('');
  const [currentFromUnit, setCurrentFromUnit] = useState<string>('');
  const [targetUnit, setTargetUnit] = useState<string>('');
  const [conversionSteps, setConversionSteps] = useState<{
    step: string;
    value: number;
    unit: string;
  }[]>([]);
  const [result, setResult] = useState<number | null>(null);
  const [resultUnit, setResultUnit] = useState<string>('');
  const [savedCalculations, setSavedCalculations] = useState<{
    steps: Step[];
    result: number;
    resultUnit: string;
    timestamp: Date;
  }[]>([]);

  const addStep = (operation: Step['operation']) => {
    if (!currentValue || !currentFromUnit) return;

    const newStep: Step = {
      value: currentValue,
      fromUnit: currentFromUnit,
      category,
      operation,
    };

    setSteps(prev => [...prev, newStep]);
    setCurrentValue('');
    setCurrentFromUnit('');
  };

  const calculateResult = () => {
    let accumulator = 0;
    let pendingOperation: '+' | '-' | '*' | '/' | null = null;
    let baseUnit = steps[0]?.fromUnit || '';
    const newSteps: { step: string; value: number; unit: string }[] = [];
    
    try {
      steps.forEach((step, index) => {
        let stepValue = Number(step.value);
        const stepUnit = step.fromUnit;

        // Add initial value to steps
        if (index === 0) {
          newSteps.push({
            step: 'Initial value',
            value: stepValue,
            unit: stepUnit
          });
        }

        // Special handling for temperature
        if (category === 'temperature') {
          if (index === 0) {
            accumulator = stepValue;
          } else {
            // Convert both values to Kelvin before operation
            const accInKelvin = convertValue(accumulator, baseUnit, 'kelvin', category);
            const stepInKelvin = convertValue(stepValue, stepUnit, 'kelvin', category);
            
            newSteps.push({
              step: `Convert ${stepValue} ${stepUnit} to Kelvin`,
              value: stepInKelvin,
              unit: 'kelvin'
            });

            switch (pendingOperation) {
              case '+': accumulator = accInKelvin + stepInKelvin - 273.15; break;
              case '-': accumulator = accInKelvin - stepInKelvin - 273.15; break;
              case '*': throw new Error('Cannot multiply temperatures');
              case '/': throw new Error('Cannot divide temperatures');
            }
            
            newSteps.push({
              step: `${pendingOperation} temperatures in Kelvin`,
              value: accumulator,
              unit: baseUnit
            });
          }
        } else {
          if (index === 0) {
            accumulator = stepValue;
            baseUnit = stepUnit;
          } else {
            // Convert to base unit before operation
            if (stepUnit !== baseUnit) {
              stepValue = convertValue(stepValue, stepUnit, baseUnit, category);
              newSteps.push({
                step: `Convert ${step.value} ${stepUnit} to ${baseUnit}`,
                value: stepValue,
                unit: baseUnit
              });
            }

            // Apply pending operation
            switch (pendingOperation) {
              case '+':
                accumulator += stepValue;
                newSteps.push({
                  step: `Add ${stepValue} ${baseUnit}`,
                  value: accumulator,
                  unit: baseUnit
                });
                break;
              case '-':
                accumulator -= stepValue;
                newSteps.push({
                  step: `Subtract ${stepValue} ${baseUnit}`,
                  value: accumulator,
                  unit: baseUnit
                });
                break;
              case '*':
                accumulator *= stepValue;
                newSteps.push({
                  step: `Multiply by ${stepValue} ${baseUnit}`,
                  value: accumulator,
                  unit: baseUnit
                });
                break;
              case '/':
                if (stepValue === 0) {
                  throw new Error('Division by zero');
                }
                accumulator /= stepValue;
                newSteps.push({
                  step: `Divide by ${stepValue} ${baseUnit}`,
                  value: accumulator,
                  unit: baseUnit
                });
                break;
            }
          }
        }
        // Store operation for next step
        pendingOperation = step.operation === '=' ? null : 
          step.operation === 'convert' ? null : step.operation;
      });

      // Convert final result to target unit if specified
      if (targetUnit) {
        accumulator = convertValue(accumulator, baseUnit, targetUnit, category);
        newSteps.push({
          step: `Convert final result to ${targetUnit}`,
          value: accumulator,
          unit: targetUnit
        });
        setResultUnit(targetUnit);
      } else {
        setResultUnit(baseUnit);
      }

      setResult(accumulator);
      setConversionSteps(newSteps);
      return accumulator;
    } catch (error) {
      setResult(null);
      setConversionSteps([]);
      alert(error instanceof Error ? error.message : 'Invalid calculation');
      return null;
    }
  };

  const clearCalculation = () => {
    setSteps([]);
    setResult(null);
    setResultUnit('');
  };

  const saveCalculation = () => {
    if (steps.length === 0 || result === null) return;

    setSavedCalculations(prev => [{
      steps: [...steps],
      result,
      resultUnit,
      timestamp: new Date()
    }, ...prev]);
  };

  const loadCalculation = (calc: { steps: Step[]; result: number; resultUnit: string }) => {
    setSteps(calc.steps);
    setResult(calc.result);
    setResultUnit(calc.resultUnit);
  };

  return (
    <div className="mt-8 p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
      <div className="mb-6 space-y-2">
        <Calculator className="w-5 h-5 text-blue-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{descriptions.title}</h2>
          <p className="text-gray-600 text-sm mt-1">{descriptions.description}</p>
        </div>
        <ul className="text-sm text-gray-500 list-disc ml-5">
          {descriptions.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      </div>

      <div className="space-y-4">
        {/* Steps Display */}
        {steps.length > 0 && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex flex-wrap gap-2 items-center">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <span className="text-gray-400">
                      {step.operation}
                    </span>
                  )}
                  <span className="px-3 py-1.5 bg-white rounded border border-gray-200">
                    {step.value} {step.fromUnit}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Input Form */}
        <div className="flex flex-wrap gap-4">
          <input
            type="number"
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            placeholder="Enter value"
            className="w-full sm:w-auto flex-1 px-4 py-2 border border-gray-200 rounded-lg"
          />
          <select
            value={currentFromUnit}
            onChange={(e) => setCurrentFromUnit(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">Unit</option>
            {unitsByCategory[category].map(unit => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>

        {/* Target Unit Selection */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Result in:</label>
          <select
            value={targetUnit}
            onChange={(e) => setTargetUnit(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">Same as first unit</option>
            {unitsByCategory[category].map(unit => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>

        {/* Operation Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={clearCalculation}
            className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => addStep('+')}
            className="p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => addStep('-')}
            className="px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100"
          >
            −
          </button>
          <button
            type="button"
            onClick={() => addStep('*')}
            className="px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100"
          >
            ×
          </button>
          <button
            type="button"
            onClick={() => addStep('/')}
            className="px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100"
          >
            ÷
          </button>
          <button
            type="button"
            onClick={calculateResult}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Equal className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={saveCalculation}
            className="p-2 bg-gray-50 rounded-lg hover:bg-gray-100"
            title="Save calculation"
          >
            <Save className="w-5 h-5" />
          </button>
        </div>

        {/* Result Display */}
        {result !== null && (
          <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg space-y-2">
            <p className="text-lg font-semibold text-gray-800">
              Result: {result.toFixed(4)} {resultUnit}
            </p>
            {conversionSteps.length > 0 && (
              <div className="mt-4 space-y-2">
                <h3 className="font-medium text-gray-700">Conversion Steps:</h3>
                <div className="space-y-2">
                  {conversionSteps.map((step, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/50 rounded-lg">
                      <span className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-medium text-sm">{index + 1}</span>
                      </span>
                      <div>
                        <p className="text-sm text-gray-700">{step.step}</p>
                        <p className="text-sm text-gray-500">
                          Result: {step.value.toFixed(4)} {step.unit}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Saved Calculations */}
        {savedCalculations.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2">
              <History className="w-4 h-4 text-gray-600" />
              <h3 className="font-medium text-gray-700">Saved Calculations</h3>
            </div>
            <div className="space-y-2">
              {savedCalculations.map((calc, index) => (
                <button
                  key={index}
                  onClick={() => loadCalculation(calc)}
                  className="w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg"
                >
                  <div className="flex flex-wrap gap-2 text-sm">
                    {calc.steps.map((step, stepIndex) => (
                      <React.Fragment key={stepIndex}>
                        {stepIndex > 0 && (
                          <span className="text-gray-400">
                            {step.operation === 'convert' ? '→' : step.operation}
                          </span>
                        )}
                        <span>{step.value} {step.fromUnit}</span>
                      </React.Fragment>
                    ))}
                    <span className="text-gray-400">=</span>
                    <span className="font-medium">{calc.result.toFixed(4)} {calc.resultUnit}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {calc.timestamp.toLocaleString()}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};